(() => {
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const parseSectionHtml = (html, selector) => {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  };

  const updateCartIcon = (sections) => {
    const currentBubble = document.getElementById('cart-icon-bubble');
    if (!currentBubble) return null;

    const bubbleHtml = sections?.['cart-icon-bubble'];
    const parsed = bubbleHtml ? new DOMParser().parseFromString(bubbleHtml, 'text/html') : null;
    const countText = parsed?.querySelector('.cart-count-bubble [aria-hidden="true"]')?.textContent?.trim() || '';
    const count = Number.parseInt(countText, 10) || 0;

    /* Clone the header control to remove event listeners owned by the old drawer.
       The newly inserted cart-drawer binds itself to this clean control. */
    const nextBubble = currentBubble.cloneNode(true);
    nextBubble.querySelector('.vvop-cart-count, .cart-count-bubble')?.remove();
    if (count > 0) {
      const badge = document.createElement('span');
      badge.className = 'vvop-cart-count';
      badge.textContent = String(count);
      nextBubble.appendChild(badge);
    }
    currentBubble.replaceWith(nextBubble);
    return nextBubble;
  };

  const fetchFreshCartSections = async (sectionIds) => {
    const cartUrl = window.routes?.cart_url || '/cart';
    const url = new URL(cartUrl, window.location.origin);
    url.searchParams.set('sections', sectionIds.join(','));

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) throw new Error('Unable to refresh the cart.');
    return response.json();
  };

  const replaceCartDrawer = (sections, activeElement) => {
    const drawerHtml = sections?.['cart-drawer'];
    const nextDrawer = parseSectionHtml(drawerHtml, 'cart-drawer');
    const currentDrawer = document.querySelector('cart-drawer');
    if (!nextDrawer || !currentDrawer) return null;

    currentDrawer.replaceWith(nextDrawer);
    const liveDrawer = document.querySelector('cart-drawer');
    if (!liveDrawer) return null;

    window.requestAnimationFrame(() => {
      try {
        liveDrawer.setActiveElement?.(activeElement || document.activeElement);
        liveDrawer.open?.(activeElement || document.getElementById('cart-icon-bubble'));
      } catch (error) {
        console.error('[Velivor] Unable to open refreshed cart drawer.', error);
        liveDrawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
      }
    });
    return liveDrawer;
  };

  const addVariantToCart = async (variantId, quantity = 1) => {
    const sectionIds = ['cart-drawer', 'cart-icon-bubble'];
    const activeElement = document.activeElement;
    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', String(Math.max(1, Number(quantity) || 1)));
    body.append('sections', sectionIds.join(','));
    body.append('sections_url', window.location.pathname || '/');

    const endpoint = window.routes?.cart_add_url || '/cart/add.js';
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body
    });

    const data = await response.json();
    if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this item.');

    let sections = data.sections || {};
    try {
      const freshSections = await fetchFreshCartSections(sectionIds);
      if (freshSections?.['cart-drawer']) sections = freshSections;
    } catch (error) {
      console.warn('[Velivor] Fresh cart refresh failed; using add response sections.', error);
    }

    updateCartIcon(sections);
    const liveDrawer = replaceCartDrawer(sections, activeElement);

    if (!liveDrawer) {
      const existingDrawer = document.querySelector('cart-drawer');
      if (existingDrawer && typeof existingDrawer.renderContents === 'function') {
        try {
          existingDrawer.renderContents({ ...data, id: data.id || variantId, sections });
        } catch (error) {
          console.error('[Velivor] Cart drawer fallback render failed.', error);
        }
      }
    }

    const state = { ...data, id: data.id || variantId, sections };
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'velivor-sticky-cart', response: state } }));
    return state;
  };

  const initBuyCard = (card) => {
    if (card.dataset.vvCommerceReady === 'true') return;
    card.dataset.vvCommerceReady = 'true';

    const variantSelect = card.querySelector('[data-vv-variant-select]');
    const variantInput = card.querySelector('[data-vv-variant-id]');
    const quantityInput = card.querySelector('[data-vv-quantity]');
    const submit = card.querySelector('[data-vv-submit]');
    const submitLabel = card.querySelector('[data-vv-submit-label]');
    const buyNow = card.querySelector('[data-vv-buy-now]');
    const buyNowLabel = card.querySelector('[data-vv-buy-now-label]');
    const price = card.querySelector('[data-vv-price]');
    const priceBox = card.querySelector('[data-vv-price-box]');
    const priceLabel = card.querySelector('[data-vv-price-label]');
    const priceSave = card.querySelector('[data-vv-price-save]');
    const compare = card.querySelector('[data-vv-compare]');
    const stock = card.querySelector('[data-vv-stock]');
    const sticky = card.querySelector('[data-vv-sticky-buy]');
    const stickyPrice = card.querySelector('[data-vv-sticky-price]');
    const stickyVariant = card.querySelector('[data-vv-sticky-variant]');
    const stickyButton = card.querySelector('[data-vv-sticky-add]');
    const stickyButtonText = card.querySelector('[data-vv-sticky-add-text]');
    const stickyBundleButtons = Array.from(card.querySelectorAll('[data-vv-sticky-bundle]'));
    const stickyStatus = card.querySelector('[data-vv-sticky-status]');
    const stickyToggle = card.querySelector('[data-vv-sticky-toggle]');
    const stickyImage = card.querySelector('.vvmb-sticky__image img');
    const bundleTotals = Array.from(card.querySelectorAll('[data-vv-bundle-total]'));
    const bundleCompares = Array.from(card.querySelectorAll('[data-vv-bundle-compare]'));
    const bundleUnits = Array.from(card.querySelectorAll('[data-vv-bundle-unit]'));
    const bundleSavings = Array.from(card.querySelectorAll('[data-vv-bundle-saving]'));
    const mainFormZone = card.querySelector('.vvmb-card') || card.querySelector('.vvmb-form') || card.querySelector('.vvmb-info');
    let mainFormVisible = false;
    let adding = false;
    let currentAvailable = true;
    let stickyCollapsed = false;
    let stickyHasAutoCollapsed = false;
    let stickyAutoCollapseScheduled = false;
    let stickyHintTimer = null;
    let stickyBounceBackTimer = null;
    let stickyCollapseTimer = null;

    const selectedMainOption = () => variantSelect?.selectedOptions?.[0] || null;
    const selectedStickyOption = () => stickyVariant?.tagName === 'SELECT' ? stickyVariant.selectedOptions?.[0] : null;
    const selectedQuantity = () => Math.max(1, Number(quantityInput?.value) || 1);

    const formatMoney = (cents) => {
      const value = Number(cents) || 0;
      const format = card.dataset.moneyFormat || '${{amount}}';
      const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts[0] + (precision ? decimal + parts[1] : '');
      };

      const tokenMatch = format.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!tokenMatch) return withDelimiters(value, 2, ' ', ',');

      const token = tokenMatch[1];
      let amount;
      switch (token) {
        case 'amount_no_decimals':
          amount = withDelimiters(value, 0, ',', '.');
          break;
        case 'amount_with_comma_separator':
          amount = withDelimiters(value, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          amount = withDelimiters(value, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          amount = withDelimiters(value, 2, "'", '.');
          break;
        case 'amount_no_decimals_with_apostrophe_separator':
          amount = withDelimiters(value, 0, "'", '.');
          break;
        case 'amount_with_space_separator':
          amount = withDelimiters(value, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          amount = withDelimiters(value, 0, ' ', ',');
          break;
        case 'amount_with_period_and_space_separator':
          amount = withDelimiters(value, 2, ' ', '.');
          break;
        case 'amount':
        default:
          amount = withDelimiters(value, 2, ',', '.');
          break;
      }
      return format.replace(tokenMatch[0], amount);
    };

    const currentPriceCents = () => Number(selectedMainOption()?.dataset?.priceCents || variantInput?.dataset?.priceCents || 0);
    const currentCompareCents = () => Number(selectedMainOption()?.dataset?.compareCents || variantInput?.dataset?.compareCents || 0);
    const bundleCompareBasis = card.dataset.bundleCompareBasis || 'regular';

    const bundleTotalForTarget = (target) => {
      if (!target) return currentPriceCents();
      const qty = Math.max(1, Number(target.dataset.qty) || 1);
      const percentage = Math.min(100, Math.max(0, Number(target.dataset.percentageDiscount) || 0));
      const fixedDiscount = Math.max(0, Number(target.dataset.fixedDiscountCents) || 0);
      const regularTotal = currentPriceCents() * qty;
      const discounted = Math.round(regularTotal * ((100 - percentage) / 100) - fixedDiscount);
      return Math.max(0, discounted);
    };

    const bundleTargetByIndex = (index) => bundleTotals.find((item) => String(item.dataset.bundleIndex) === String(index));
    const selectedBundleRadio = () => card.querySelector('[data-vv-quantity-picker] input:checked');
    const selectedBundleTarget = () => bundleTargetByIndex(selectedBundleRadio()?.dataset?.bundleIndex);

    const syncStickyBundleButtons = () => {
      const selectedIndex = String(selectedBundleRadio()?.dataset?.bundleIndex || '');
      stickyBundleButtons.forEach((button) => {
        const active = String(button.dataset.bundleIndex || '') === selectedIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const bundleCompareForTarget = (target, total) => {
      if (!target || bundleCompareBasis === 'none') return 0;
      const qty = Math.max(1, Number(target.dataset.qty) || 1);
      const unit = currentPriceCents();
      const compareUnit = currentCompareCents();
      const compareTotal = bundleCompareBasis === 'compare_at' && compareUnit > unit
        ? compareUnit * qty
        : unit * qty;
      return compareTotal > total ? compareTotal : 0;
    };

    const updateBundlePrices = () => {
      bundleTotals.forEach((target) => {
        target.textContent = formatMoney(bundleTotalForTarget(target));
      });
      bundleCompares.forEach((target) => {
        const source = bundleTargetByIndex(target.dataset.bundleIndex);
        const total = bundleTotalForTarget(source);
        const compareTotal = bundleCompareForTarget(source, total);
        target.hidden = compareTotal <= 0;
        target.textContent = compareTotal > 0 ? formatMoney(compareTotal) : '';
      });
      bundleUnits.forEach((target) => {
        const source = bundleTargetByIndex(target.dataset.bundleIndex);
        const qty = Math.max(1, Number(source?.dataset?.qty) || 1);
        const each = Math.round(bundleTotalForTarget(source) / qty);
        target.textContent = `${formatMoney(each)} / st`;
      });
      bundleSavings.forEach((target) => {
        const source = bundleTargetByIndex(target.dataset.bundleIndex);
        const total = bundleTotalForTarget(source);
        const compareTotal = bundleCompareForTarget(source, total);
        const saving = Math.max(0, compareTotal - total);
        target.hidden = saving <= 0;
        target.textContent = saving > 0 ? `Spara ${formatMoney(saving)}` : '';
      });
      if (stickyPrice) {
        const selectedTarget = selectedBundleTarget();
        stickyPrice.textContent = selectedTarget ? formatMoney(bundleTotalForTarget(selectedTarget)) : formatMoney(currentPriceCents() * selectedQuantity());
      }
      syncStickyBundleButtons();
    };

    const updateStickyMedia = (option) => {
      const image = option?.dataset?.image;
      if (stickyImage && image) stickyImage.src = image;
    };

    const updateVariant = ({ source = 'main' } = {}) => {
      let option = selectedMainOption();

      if (source === 'sticky' && stickyVariant) {
        const variantId = stickyVariant.value;
        if (variantSelect && variantSelect.value !== variantId) {
          variantSelect.value = variantId;
          option = selectedMainOption();
        } else if (!variantSelect) {
          option = selectedStickyOption();
        }
      }

      const variantId = option?.value || stickyVariant?.value || variantInput?.value || '';
      const availableValue = option?.dataset?.available ?? selectedStickyOption()?.dataset?.available;
      currentAvailable = availableValue !== 'false' && Boolean(variantId);

      if (variantInput && variantId) {
        variantInput.value = variantId;
        if (option?.dataset?.priceCents) variantInput.dataset.priceCents = option.dataset.priceCents;
        variantInput.dataset.compareCents = option?.dataset?.compareCents || '0';
      }
      if (stickyVariant && stickyVariant.value !== variantId) stickyVariant.value = variantId;

      const livePrice = option?.dataset?.price || selectedStickyOption()?.dataset?.price || '';
      if (price && livePrice) price.textContent = livePrice;

      if (compare) {
        const compareValue = option?.dataset?.compare || '';
        compare.textContent = compareValue;
        compare.hidden = !compareValue;
      }

      const livePriceCents = currentPriceCents();
      const liveCompareCents = currentCompareCents();
      const isSale = liveCompareCents > livePriceCents && liveCompareCents > 0;
      priceBox?.classList.toggle('is-sale', isSale);
      if (priceLabel) priceLabel.textContent = isSale ? 'Kampanjpris' : 'Pris';
      if (priceSave) {
        const percent = isSale ? Math.round(((liveCompareCents - livePriceCents) / liveCompareCents) * 100) : 0;
        priceSave.hidden = !isSale;
        priceSave.textContent = isSale ? `Spara ${percent}%` : '';
      }

      updateStickyMedia(selectedStickyOption());
      updateBundlePrices();

      if (submit && !adding) submit.disabled = !currentAvailable;
      if (buyNow && !adding) buyNow.disabled = !currentAvailable;
      if (stickyButton && !adding) stickyButton.disabled = !currentAvailable;
      if (submitLabel && !adding) submitLabel.textContent = currentAvailable ? (card.dataset.addLabel || 'Lägg i varukorgen') : (card.dataset.soldOutText || 'Slut i lager');
      if (buyNowLabel && !adding) buyNowLabel.textContent = currentAvailable ? (buyNowLabel.dataset.defaultLabel || buyNowLabel.textContent || 'Köp nu') : (card.dataset.soldOutText || 'Slut i lager');
      if (stickyButtonText && !adding) stickyButtonText.textContent = currentAvailable ? (sticky.dataset.vvStickyAddLabel || 'Lägg i varukorgen') : (card.dataset.soldOutText || 'Slut i lager');

      if (stock) {
        stock.classList.toggle('is-sold-out', !currentAvailable);
        stock.innerHTML = currentAvailable
          ? `<span aria-hidden="true"></span>${card.dataset.availableText || 'Kan beställas'}`
          : (card.dataset.soldOutText || 'Tillfälligt slut');
      }
    };

    variantSelect?.addEventListener('change', () => updateVariant({ source: 'main' }));
    stickyVariant?.addEventListener('change', () => {
      updateVariant({ source: 'sticky' });
      variantSelect?.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const initialBundleRadio = selectedBundleRadio();
    if (initialBundleRadio && quantityInput) quantityInput.value = initialBundleRadio.value;

    card.querySelectorAll('[data-vv-quantity-picker] input').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked && quantityInput) {
          quantityInput.value = radio.value;
          updateBundlePrices();
        }
      });
    });

    stickyBundleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const index = String(button.dataset.bundleIndex || '');
        const radio = card.querySelector(`[data-vv-quantity-picker] input[data-bundle-index="${index}"]`);
        if (!radio || radio.checked) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    const clearStickyTimers = () => {
      [stickyHintTimer, stickyBounceBackTimer, stickyCollapseTimer].forEach((timer) => {
        if (timer) window.clearTimeout(timer);
      });
      stickyHintTimer = null;
      stickyBounceBackTimer = null;
      stickyCollapseTimer = null;
    };

    const showStickyToggle = () => {
      if (!stickyToggle) return;
      stickyToggle.hidden = false;
      window.requestAnimationFrame(() => stickyToggle.classList.add('is-visible'));
      stickyToggle.setAttribute('aria-expanded', 'false');
    };

    const hideStickyToggle = () => {
      if (!stickyToggle) return;
      stickyToggle.classList.remove('is-visible');
      stickyToggle.setAttribute('aria-expanded', 'true');
    };

    const collapseSticky = () => {
      if (!sticky || !sticky.classList.contains('is-visible')) return;
      stickyCollapsed = true;
      sticky.classList.remove('is-hinting');
      sticky.classList.add('is-collapsed');
      sticky.setAttribute('aria-hidden', 'true');
      showStickyToggle();
    };

    const expandSticky = () => {
      if (!sticky) return;
      clearStickyTimers();
      stickyCollapsed = false;
      sticky.classList.remove('is-collapsed', 'is-hinting');
      sticky.classList.add('is-visible');
      sticky.setAttribute('aria-hidden', 'false');
      hideStickyToggle();
    };

    const scheduleStickyAutoCollapse = () => {
      if (!sticky || stickyHasAutoCollapsed || stickyAutoCollapseScheduled) return;
      stickyAutoCollapseScheduled = true;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        stickyCollapseTimer = window.setTimeout(() => {
          stickyHasAutoCollapsed = true;
          collapseSticky();
        }, 1800);
        return;
      }
      clearStickyTimers();
      stickyHintTimer = window.setTimeout(() => {
        sticky.classList.add('is-hinting');
      }, 1250);
      stickyBounceBackTimer = window.setTimeout(() => {
        sticky.classList.remove('is-hinting');
      }, 1430);
      stickyCollapseTimer = window.setTimeout(() => {
        stickyHasAutoCollapsed = true;
        collapseSticky();
      }, 1750);
    };

    const updateStickyVisibility = () => {
      if (!sticky) return;
      const shouldShow = mobileQuery.matches && window.scrollY > 40 && !mainFormVisible;

      if (!shouldShow) {
        clearStickyTimers();
        if (!stickyHasAutoCollapsed) stickyAutoCollapseScheduled = false;
        sticky.classList.remove('is-visible', 'is-hinting', 'is-collapsed');
        sticky.setAttribute('aria-hidden', 'true');
        if (stickyToggle) {
          stickyToggle.classList.remove('is-visible');
          stickyToggle.setAttribute('aria-expanded', 'false');
        }
        return;
      }

      sticky.classList.add('is-visible');
      if (stickyCollapsed || stickyHasAutoCollapsed) {
        stickyCollapsed = true;
        sticky.classList.add('is-collapsed');
        sticky.setAttribute('aria-hidden', 'true');
        showStickyToggle();
      } else {
        sticky.classList.remove('is-collapsed');
        sticky.setAttribute('aria-hidden', 'false');
        hideStickyToggle();
        scheduleStickyAutoCollapse();
      }
    };

    if (sticky) {
      sticky.hidden = false;
      sticky.setAttribute('aria-hidden', 'true');
      if (stickyToggle) {
        stickyToggle.hidden = false;
        stickyToggle.classList.remove('is-visible');
        stickyToggle.setAttribute('aria-expanded', 'false');
        stickyToggle.addEventListener('click', () => {
          expandSticky();
        });
      }

      if (mainFormZone && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => {
          mainFormVisible = entry.isIntersecting;
          updateStickyVisibility();
        }, {
          threshold: 0.05,
          rootMargin: '-12% 0px -12% 0px'
        });
        observer.observe(mainFormZone);
      }

      // Any deliberate interaction keeps the dock open so it never disappears while choosing a size or package.
      ['pointerdown', 'focusin', 'change'].forEach((eventName) => {
        sticky.addEventListener(eventName, () => clearStickyTimers(), { passive: eventName === 'pointerdown' });
      });

      window.addEventListener('scroll', updateStickyVisibility, { passive: true });
      mobileQuery.addEventListener?.('change', updateStickyVisibility);
      updateStickyVisibility();
    }

    // Main purchase actions: keep Add to cart inside the storefront and open the side cart.
    submit?.addEventListener('click', async (event) => {
      event.preventDefault();
      if (adding || !currentAvailable) return;
      const variantId = variantInput?.value || variantSelect?.value || stickyVariant?.value;
      if (!variantId) return;

      adding = true;
      submit.disabled = true;
      if (buyNow) buyNow.disabled = true;
      submit.classList.add('is-loading');
      const defaultLabel = card.dataset.addLabel || 'Lägg i varukorgen';
      if (submitLabel) submitLabel.textContent = 'Lägger till…';

      try {
        await addVariantToCart(variantId, selectedQuantity());
        if (submitLabel) submitLabel.textContent = 'Tillagd ✓';
        window.setTimeout(() => {
          if (submitLabel) submitLabel.textContent = defaultLabel;
        }, 1600);
      } catch (error) {
        if (submitLabel) submitLabel.textContent = 'Försök igen';
        console.error('[Velivor] Add to cart failed.', error);
        window.setTimeout(() => {
          if (submitLabel) submitLabel.textContent = defaultLabel;
        }, 2200);
      } finally {
        adding = false;
        submit.classList.remove('is-loading');
        submit.disabled = !currentAvailable;
        if (buyNow) buyNow.disabled = !currentAvailable;
      }
    });

    // Direct purchase: add the selected size/bundle, then continue straight to Shopify checkout.
    buyNow?.addEventListener('click', async (event) => {
      event.preventDefault();
      if (adding || !currentAvailable) return;
      const variantId = variantInput?.value || variantSelect?.value || stickyVariant?.value;
      if (!variantId) return;

      adding = true;
      buyNow.disabled = true;
      if (submit) submit.disabled = true;
      buyNow.classList.add('is-loading');
      const defaultLabel = buyNowLabel?.textContent || 'Köp nu';
      if (buyNowLabel) buyNowLabel.textContent = 'Går till kassan…';

      try {
        const body = new FormData();
        body.append('id', String(variantId));
        body.append('quantity', String(selectedQuantity()));
        const endpoint = window.routes?.cart_add_url || '/cart/add.js';
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Det gick inte att lägga till produkten.');
        window.location.assign('/checkout');
      } catch (error) {
        console.error('[Velivor] Direct checkout failed.', error);
        if (buyNowLabel) buyNowLabel.textContent = 'Försök igen';
        window.setTimeout(() => {
          if (buyNowLabel) buyNowLabel.textContent = defaultLabel;
        }, 2200);
        adding = false;
        buyNow.classList.remove('is-loading');
        buyNow.disabled = !currentAvailable;
        if (submit) submit.disabled = !currentAvailable;
      }
    });

    stickyButton?.addEventListener('click', async () => {
      if (adding || !currentAvailable) return;
      const variantId = stickyVariant?.value || variantInput?.value;
      if (!variantId) return;

      adding = true;
      stickyButton.disabled = true;
      stickyButton.classList.add('is-loading');
      if (stickyButtonText) stickyButtonText.textContent = 'Adding…';
      if (stickyStatus) stickyStatus.textContent = 'Adding product to cart';

      try {
        await addVariantToCart(variantId, quantityInput?.value || 1);
        const successLabel = sticky.dataset.vvStickyAddedLabel || 'Added ✓';
        if (stickyButtonText) stickyButtonText.textContent = successLabel;
        if (stickyStatus) stickyStatus.textContent = 'Product added to cart';
        window.setTimeout(() => {
          if (stickyButtonText) stickyButtonText.textContent = sticky.dataset.vvStickyAddLabel || 'Add to cart';
          if (stickyStatus) stickyStatus.textContent = '';
        }, 1800);
      } catch (error) {
        const errorLabel = sticky.dataset.vvStickyErrorLabel || 'Try again';
        if (stickyButtonText) stickyButtonText.textContent = errorLabel;
        if (stickyStatus) stickyStatus.textContent = error.message || 'Unable to add product';
        window.setTimeout(() => {
          if (stickyButtonText) stickyButtonText.textContent = sticky.dataset.vvStickyAddLabel || 'Add to cart';
        }, 2200);
      } finally {
        adding = false;
        stickyButton.classList.remove('is-loading');
        stickyButton.disabled = !currentAvailable;
      }
    });

    updateVariant();
  };

  const initProductSticky = (sticky) => {
    if (sticky.dataset.vvProductStickyReady === 'true') return;
    sticky.dataset.vvProductStickyReady = 'true';

    const productSection = sticky.closest('section');
    const productZone = productSection?.querySelector('.product__info-container') || productSection?.querySelector('.product-form');
    const variantSelect = sticky.querySelector('[data-vv-product-sticky-variant]');
    const addButton = sticky.querySelector('[data-vv-product-sticky-add]');
    const addText = sticky.querySelector('[data-vv-product-sticky-add-text]');
    const status = sticky.querySelector('[data-vv-product-sticky-status]');
    const priceTarget = sticky.querySelector('[data-vv-product-sticky-price]');
    const imageTarget = sticky.querySelector('.vv-product-sticky__image img');
    let productZoneVisible = false;
    let adding = false;

    const selectedOption = () => variantSelect?.tagName === 'SELECT' ? variantSelect.selectedOptions?.[0] : variantSelect;

    const updateVariant = () => {
      const option = selectedOption();
      const available = option?.dataset?.available !== 'false' && Boolean(variantSelect?.value);
      if (priceTarget && option?.dataset?.price) priceTarget.textContent = option.dataset.price;
      if (imageTarget && option?.dataset?.image) imageTarget.src = option.dataset.image;
      if (addButton && !adding) addButton.disabled = !available;
      if (addText && !adding) addText.textContent = available ? (sticky.dataset.vvAddLabel || 'Add to cart') : (sticky.dataset.vvSoldOutLabel || 'Sold out');
    };

    const updateVisibility = () => {
      const shouldShow = mobileQuery.matches && window.scrollY > 40 && !productZoneVisible;
      sticky.classList.toggle('is-visible', shouldShow);
      sticky.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    };

    sticky.hidden = false;
    sticky.setAttribute('aria-hidden', 'true');
    variantSelect?.addEventListener('change', updateVariant);

    if (productZone && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        productZoneVisible = entry.isIntersecting;
        updateVisibility();
      }, { threshold: 0.04, rootMargin: '-10% 0px -10% 0px' });
      observer.observe(productZone);
    }

    window.addEventListener('scroll', updateVisibility, { passive: true });
    mobileQuery.addEventListener?.('change', updateVisibility);

    addButton?.addEventListener('click', async () => {
      const variantId = variantSelect?.value;
      if (!variantId || adding || addButton.disabled) return;
      adding = true;
      addButton.disabled = true;
      addButton.classList.add('is-loading');
      if (addText) addText.textContent = 'Adding…';
      if (status) status.textContent = 'Adding product to cart';

      try {
        await addVariantToCart(variantId, 1);
        if (addText) addText.textContent = sticky.dataset.vvAddedLabel || 'Added ✓';
        if (status) status.textContent = 'Product added to cart';
        window.setTimeout(() => {
          if (addText) addText.textContent = sticky.dataset.vvAddLabel || 'Add to cart';
          if (status) status.textContent = '';
        }, 1800);
      } catch (error) {
        if (addText) addText.textContent = sticky.dataset.vvErrorLabel || 'Try again';
        if (status) status.textContent = error.message || 'Unable to add product';
        window.setTimeout(() => {
          if (addText) addText.textContent = sticky.dataset.vvAddLabel || 'Add to cart';
        }, 2200);
      } finally {
        adding = false;
        addButton.classList.remove('is-loading');
        updateVariant();
      }
    });

    updateVariant();
    updateVisibility();
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-vv-buy-card]').forEach(initBuyCard);
    scope.querySelectorAll('[data-vv-product-sticky]').forEach(initProductSticky);
  };

  ready(() => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
